import React, { useState, useEffect, useCallback, useRef } from 'react';
import { fetchSpeedTestAudit } from '../services/speedTestAuditApi';
import {
  FaCheckCircle,
  FaTimesCircle,
  FaKey,
  FaInbox,
  FaExclamationTriangle,
  FaSyncAlt,
  FaFilter,
} from 'react-icons/fa';

const EVENT_TYPES = [
  { value: '', label: 'All events' },
  { value: 'results_received', label: 'Accepted (results received)' },
  { value: 'token_issued', label: 'Token issued' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'requested', label: 'Requested' },
  { value: 'failed', label: 'Failed' },
  { value: 'error', label: 'Error' },
];

const TIME_OPTIONS = [
  { value: 60, label: 'Last 1 hour' },
  { value: 240, label: 'Last 4 hours' },
  { value: 1440, label: 'Last 24 hours' },
  { value: 4320, label: 'Last 3 days' },
  { value: 10080, label: 'Last 7 days' },
];

function EventBadge({ eventType }) {
  const t = (eventType || '').toLowerCase();
  if (t === 'results_received')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        <FaCheckCircle /> Accepted
      </span>
    );
  if (t === 'token_issued')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
        <FaKey /> Token issued
      </span>
    );
  if (t === 'rejected')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
        <FaTimesCircle /> Rejected
      </span>
    );
  if (t === 'requested')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        <FaInbox /> Requested
      </span>
    );
  if (t === 'failed' || t === 'error')
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
        <FaExclamationTriangle /> {eventType}
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      {eventType || '—'}
    </span>
  );
}

/**
 * Map audit reason code to human-readable label (rejected vs enrolled).
 */
function reasonLabel(reason) {
  if (!reason) return '—';
  const r = String(reason).toLowerCase();
  if (r === 'enrolled') return 'Enrolled';
  if (r === 'device_not_enrolled') return 'Device not enrolled';
  if (r === 'manual_speed_test_disabled') return 'Manual speed test disabled';
  if (r === 'auto_speed_test_disabled') return 'Auto speed test disabled';
  if (r === 'kill_switch_manual_disabled') return 'Kill switch: manual disabled';
  if (r === 'kill_switch_auto_disabled') return 'Kill switch: auto disabled';
  if (r === 'uuid_required') return 'UUID required';
  if (r.startsWith('uem_lookup_failed')) return 'UEM lookup failed';
  if (r === 'token_uuid_mismatch') return 'Token UUID mismatch';
  if (r === 'results_saved') return 'Results saved';
  return reason;
}

/**
 * Format audit event_time for display in 12-hour AM/PM CST.
 * Backend may send "M/D/YYYY, h:mm:ss AM/PM CT" or "YYYY-MM-DD HH:mm:ss CT"; normalize to 12-hour.
 */
function formatEventTime(str) {
  if (!str) return '—';
  const s = String(str).trim();
  try {
    let d;
    if (s.endsWith(' CT')) {
      const trimmed = s.replace(/\s*CT\s*$/, '').trim();
      if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}:\d{2}$/.test(trimmed)) {
        d = new Date(trimmed.replace(' ', 'T') + '-06:00');
      } else {
        d = new Date(trimmed);
      }
    } else {
      const utcLike = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\s*Z?)?$/;
      const dateStr = utcLike.test(s) && !s.endsWith('Z') ? s.replace(' ', 'T') + 'Z' : s;
      d = new Date(dateStr);
    }
    if (Number.isNaN(d.getTime())) return str;
    return d.toLocaleString('en-US', {
      timeZone: 'America/Chicago',
      dateStyle: 'short',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }) + ' CT';
  } catch {
    return str;
  }
}

export default function SpeedTestAuditPage() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({
    rejected: 0,
    token_issued: 0,
    results_received: 0,
    requested: 0,
    failed: 0,
    error: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minutes, setMinutes] = useState(240);
  const [limit] = useState(200);
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [androidIdFilter, setAndroidIdFilter] = useState('');
  const [friendlyNameFilter, setFriendlyNameFilter] = useState('');
  const [debouncedAndroidIdFilter, setDebouncedAndroidIdFilter] = useState('');
  const [debouncedFriendlyNameFilter, setDebouncedFriendlyNameFilter] = useState('');
  const abortControllerRef = useRef(null);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedAndroidIdFilter(androidIdFilter), 400);
    return () => clearTimeout(timeoutId);
  }, [androidIdFilter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedFriendlyNameFilter(friendlyNameFilter), 400);
    return () => clearTimeout(timeoutId);
  }, [friendlyNameFilter]);

  const load = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSpeedTestAudit({
        minutes,
        limit,
        event_type: eventTypeFilter || undefined,
        android_id: debouncedAndroidIdFilter || undefined,
        friendly_name: debouncedFriendlyNameFilter || undefined,
        signal: controller.signal,
      });
      const auditRows = data.rows || [];
      setRows(auditRows);
      setSummary(data.summary || {});
      console.log(
        '[MAI Speed test] Backend OK — GET /maiApi/speed-test/audit succeeded (API reached; DB query completed on server).'
      );
      if (auditRows.length === 0) {
        console.log(
          '[MAI Speed test] No data: no audit events in the selected time window / filters.'
        );
      } else {
        console.log(`[MAI Speed test] Audit table: ${auditRows.length} event(s).`);
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.warn('[MAI Speed test] GET /maiApi/speed-test/audit failed:', e?.message || e);
      setError(e.message || 'Failed to load audit');
      setRows([]);
      setSummary({ rejected: 0, token_issued: 0, results_received: 0, requested: 0, failed: 0, error: 0 });
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [minutes, limit, eventTypeFilter, debouncedAndroidIdFilter, debouncedFriendlyNameFilter]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  return (
    <div className="w-full px-4 py-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-800">Speed Test Audit</h1>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <FaSyncAlt className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <div className="text-xs font-medium text-green-700 uppercase">Accepted</div>
          <div className="text-xl font-bold text-green-800">{summary.results_received ?? 0}</div>
          <div className="text-xs text-green-600">results_received</div>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-xs font-medium text-blue-700 uppercase">Token issued</div>
          <div className="text-xl font-bold text-blue-800">{summary.token_issued ?? 0}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="text-xs font-medium text-red-700 uppercase">Rejected</div>
          <div className="text-xl font-bold text-red-800">{summary.rejected ?? 0}</div>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
          <div className="text-xs font-medium text-gray-700 uppercase">Requested</div>
          <div className="text-xl font-bold text-gray-800">{summary.requested ?? 0}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs font-medium text-amber-700 uppercase">Failed</div>
          <div className="text-xl font-bold text-amber-800">{summary.failed ?? 0}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="text-xs font-medium text-amber-700 uppercase">Error</div>
          <div className="text-xl font-bold text-amber-800">{summary.error ?? 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700">
          <FaFilter /> Filters
        </span>
        <label className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Time</span>
          <select
            value={minutes}
            onChange={(e) => setMinutes(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {TIME_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Event</span>
          <select
            value={eventTypeFilter}
            onChange={(e) => setEventTypeFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm"
          >
            {EVENT_TYPES.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Android ID</span>
          <input
            type="text"
            placeholder="Filter by Android ID"
            value={androidIdFilter}
            onChange={(e) => setAndroidIdFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Friendly name</span>
          <input
            type="text"
            placeholder="Search by device name"
            value={friendlyNameFilter}
            onChange={(e) => setFriendlyNameFilter(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1 text-sm w-44"
          />
        </label>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-12">
          <FaSyncAlt className="animate-spin text-3xl text-blue-600" />
        </div>
      )}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-lg border border-gray-300 bg-white shadow">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100 border-b border-gray-300">
                <th className="text-left p-3 font-semibold text-gray-700">Time</th>
                <th className="text-left p-3 font-semibold text-gray-700">Android ID</th>
                <th className="text-left p-3 font-semibold text-gray-700">Friendly name</th>
                <th className="text-left p-3 font-semibold text-gray-700">Event</th>
                <th className="text-left p-3 font-semibold text-gray-700">Test type</th>
                <th className="text-left p-3 font-semibold text-gray-700">Connection</th>
                <th className="text-left p-3 font-semibold text-gray-700">Success</th>
                <th className="text-left p-3 font-semibold text-gray-700">Reason</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-gray-500">
                    No audit events in the selected time window.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                  <tr
                    key={`${r.event_time}-${r.android_id}-${i}`}
                    className="border-b border-gray-200 hover:bg-gray-50"
                  >
                    <td className="p-3 text-gray-700 whitespace-nowrap">
                      {formatEventTime(r.event_time)}
                    </td>
                    <td className="p-3 font-mono text-gray-800">{r.android_id || '—'}</td>
                    <td className="p-3 text-gray-800">{r.device_friendly_name || '—'}</td>
                    <td className="p-3">
                      <EventBadge eventType={r.event_type} />
                    </td>
                    <td className="p-3 text-gray-600">{r.test_type || '—'}</td>
                    <td className="p-3 text-gray-600">
                      {r.connection_type && r.connection_type !== 'N/A' ? r.connection_type : 'N/A'}
                    </td>
                    <td className="p-3">
                      {r.success ? (
                        <span className="text-green-600">Yes</span>
                      ) : (
                        <span className="text-red-600">No</span>
                      )}
                    </td>
                    <td className="p-3 text-gray-600 max-w-xs truncate" title={r.reason || ''}>
                      {reasonLabel(r.reason)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="mt-3 text-sm text-gray-500">
          Showing {rows.length} event{rows.length !== 1 ? 's' : ''}.
        </div>
      )}
    </div>
  );
}
